import { useEffect, useRef, useState, useCallback } from "react";
import { Heart, Flame, Lightbulb, Rocket, HandMetal, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { useLang, useAuth } from "@/context/AppContext";
import { useLiveEvent } from "@/context/RealtimeContext";
import { Input } from "@/components/ui/input";

const REACTIONS = [["heart", Heart, "text-rose-400"], ["clap", HandMetal, "text-yellow-300"], ["fire", Flame, "text-orange-400"], ["idea", Lightbulb, "text-cyan-300"], ["rocket", Rocket, "text-emerald-300"]];
export const REACTION_ICONS = Object.fromEntries(REACTIONS.map(([k, I, c]) => [k, [I, c]]));
const EMPTY_THREAD = { items: [], reactions: {} };

const Floater = ({ f }) => {
  const [I, c] = REACTION_ICONS[f.emoji];
  return <I className={`floater pointer-events-none absolute bottom-2 h-6 w-6 ${c}`} style={{ left: `${f.x}%` }} />;
};

const nameColor = (uid, me, host) => {
  if (uid === me) return "text-emerald-300";
  if (uid === host) return "text-yellow-300";
  return "text-cyan-300";
};

const addUnique = (xs, item) => (xs.some((x) => x.id === item.id) ? xs : [...xs, item]);

const useFloaters = () => {
  const [floats, setFloats] = useState([]);
  const spawn = useCallback((emoji) => {
    const f = { id: Math.random(), emoji, x: 10 + Math.random() * 80 };
    setFloats((xs) => [...xs, f]);
    setTimeout(() => setFloats((xs) => xs.filter((x) => x.id !== f.id)), 2200);
  }, []);
  return { floats, spawn };
};

const useBroadcastThread = (bid, spawn) => {
  const { data, setData } = useApi(`/broadcasts/${bid}/comments`, { initial: EMPTY_THREAD });
  const addComment = useCallback((c) => setData((d) => ({ ...d, items: addUnique(d.items, c) })), [setData]);
  useLiveEvent(["broadcast_comment", "broadcast_reaction"], (m) => {
    if (m.data.broadcast_id !== bid) return;
    if (m.kind === "broadcast_comment") return addComment(m.data);
    setData((d) => ({ ...d, reactions: m.data.reactions }));
    spawn(m.data.emoji);
  });
  return { items: data.items, counts: data.reactions, addComment };
};

const CommentList = ({ items, me, host }) => {
  const { t } = useLang();
  const list = useRef(null);
  useEffect(() => { if (list.current) list.current.scrollTop = list.current.scrollHeight; }, [items.length]);
  return (
    <div ref={list} className="h-56 space-y-2 overflow-y-auto pr-1" data-testid="broadcast-comments">
      {!items.length && <p className="text-sm text-slate-500">{t("chat_empty")}</p>}
      {items.map((c) => (
        <p key={c.id} className="text-sm" data-testid={`broadcast-comment-${c.id}`}>
          <span className={`font-semibold ${nameColor(c.user_id, me, host)}`}>{c.name}</span>
          <span className="ml-2 text-slate-200">{c.text}</span>
        </p>
      ))}
    </div>
  );
};

const ReactionBar = ({ counts, onReact }) => (
  <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="broadcast-reactions">
    {REACTIONS.map(([k, I, c]) => (
      <button key={k} onClick={() => onReact(k)} data-testid={`reaction-btn-${k}`}
        className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1 text-xs text-slate-300 transition-transform duration-150 hover:scale-110 active:scale-95">
        <I className={`h-4 w-4 ${c}`} /><span className="font-mono" data-testid={`reaction-count-${k}`}>{counts[k] || 0}</span>
      </button>
    ))}
  </div>
);

const CommentForm = ({ bid, onSent }) => {
  const { t } = useLang();
  const [text, setText] = useState("");
  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try { const { data } = await api.post(`/broadcasts/${bid}/comments`, { text }); setText(""); onSent(data); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={send} className="mt-3 flex gap-2">
      <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={t("comment_placeholder")} maxLength={500} data-testid="broadcast-comment-input" />
      <button disabled={!text.trim()} data-testid="broadcast-comment-send" className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-emerald-500 text-slate-950 disabled:opacity-40"><Send className="h-4 w-4" /></button>
    </form>
  );
};

export const BroadcastLive = ({ b }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const { floats, spawn } = useFloaters();
  const { items, counts, addComment } = useBroadcastThread(b.id, spawn);
  const react = async (emoji) => { try { await api.post(`/broadcasts/${b.id}/reactions/${emoji}`); } catch (err) { toast.error(errMsg(err)); } };

  return (
    <div className="relative mt-4 overflow-hidden rounded-md border border-rose-400/20 bg-[#0B0F17] p-4" data-testid={`broadcast-live-${b.id}`}>
      {floats.map((f) => <Floater key={f.id} f={f} />)}
      <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-rose-300">{t("comments")} · {b.title}</p>
      <CommentList items={items} me={user.id} host={b.host_id} />
      <ReactionBar counts={counts} onReact={react} />
      <CommentForm bid={b.id} onSent={addComment} />
    </div>
  );
};
